import apiClient from './api';
import type { MaterielDeneignement } from '@/types';

const BASE_URL = '/materiels';

export interface CreateMaterielDTO {
  nom: string;
  type: string;
  immatriculation?: string;
  disponible?: boolean;
}

export interface UpdateMaterielDTO extends Partial<CreateMaterielDTO> {}

export interface MaterielFilters {
  type?: string;
  disponible?: boolean;
}

export interface MaterielStats {
  total: number;
  disponibles: number;
  indisponibles: number;
  parType: Record<string, number>;
}

export const MATERIEL_TYPES = [
  { value: 'chasse_neige', label: 'Chasse-neige' },
  { value: 'saleuse', label: 'Saleuse' },
  { value: 'balayeuse', label: 'Balayeuse' },
  { value: 'souffleuse', label: 'Souffleuse' },
  { value: 'vehicule', label: 'Vehicule' },
  { value: 'autre', label: 'Autre' },
];

/**
 * Service API pour le materiel de deneigement
 */
export const materielService = {
  /**
   * Liste du materiel avec filtres
   */
  async list(filters: MaterielFilters = {}): Promise<MaterielDeneignement[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await apiClient.get<MaterielDeneignement[]>(
      `${BASE_URL}?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Detail d'un materiel
   */
  async get(id: number): Promise<MaterielDeneignement> {
    const response = await apiClient.get<MaterielDeneignement>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Creation d'un materiel
   */
  async create(data: CreateMaterielDTO): Promise<MaterielDeneignement> {
    const response = await apiClient.post<MaterielDeneignement>(BASE_URL, data);
    return response.data;
  },

  /**
   * Modification d'un materiel
   */
  async update(id: number, data: UpdateMaterielDTO): Promise<MaterielDeneignement> {
    const response = await apiClient.put<MaterielDeneignement>(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  /**
   * Suppression d'un materiel
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Materiel disponible
   */
  async getDisponibles(): Promise<MaterielDeneignement[]> {
    const response = await apiClient.get<MaterielDeneignement[]>(`${BASE_URL}/disponibles`);
    return response.data;
  },

  /**
   * Modifier la disponibilite
   */
  async updateDisponibilite(id: number, disponible: boolean): Promise<MaterielDeneignement> {
    const response = await apiClient.patch<MaterielDeneignement>(
      `${BASE_URL}/${id}/disponibilite`,
      { disponible }
    );
    return response.data;
  },

  /**
   * Liste des types de materiel
   */
  async getTypes(): Promise<string[]> {
    const response = await apiClient.get<string[]>(`${BASE_URL}/types/list`);
    return response.data;
  },

  /**
   * Statistiques globales
   */
  async getStats(): Promise<MaterielStats> {
    const response = await apiClient.get<MaterielStats>(`${BASE_URL}/stats/global`);
    return response.data;
  },
};

export default materielService;
